/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {ColDef} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {
  VehicleScenarioSelectPopUpComponent
} from '../../../core/components/vehicle-scenario-select/vehicle-scenario-select-pop-up/vehicle-scenario-select-pop-up.component';
import {getDefaultYearRange, range} from '../../../core/helpers/generic-helpers';
import {LoadingCounter} from '../../../core/helpers/loading-counter';
import {StateService} from '../../../core/state/state.service';
import {GlobalScenario} from '../../../models/global-scenario-dto.model';
import {PivotTablePropDataRequestDTO} from '../../../models/pivot-table-prop-data-request-dto.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';

@Component({
    selector: 'app-pivot-table-data-propulsion',
    templateUrl: './pivot-table-data-propulsion.component.html',
    styleUrl: './pivot-table-data-propulsion.component.scss',
    standalone: false
})
export class PivotTableDataPropulsionComponent implements OnInit {

  readonly loading = new LoadingCounter();
  showPlanningMarket: boolean = true;

  selectedScenarioType: string;
  selectedGeography: number;

  selectedWorkingScenario: VehicleScenario;
  selectedGlobalScenario: GlobalScenario;

  // Geography flags
  SUBREGION_FLAG = 1;
  MKTAREA_FLAG = 2;
  PLNGMKT_FLAG = 3;

  REGION = 'REGION';
  SUBREGION = 'SUBREGION';
  MKTAREA = 'MKTAREA';
  MKTDIV = 'MKTDIV';
  PLNGMKT = 'PLNGMKT';
  BRAND = 'BRAND';
  PRGMCODE = 'PRGMCODE';

  allFilterOptions: any[] = [];
  filteredData: any[] = [];
  regionOptions: SelectableOptions = new SelectableOptions();
  geographyOptions: SelectableOptions = new SelectableOptions();
  mktDivOptions: SelectableOptions = new SelectableOptions();
  prgmCodeOptions: SelectableOptions = new SelectableOptions();
  brandOptions: SelectableOptions = new SelectableOptions();


  memberScenarioColumns: ColDef[] = [
    {field: 'vehScenId', headerName: 'Vehicle Scenario ID', lockPosition: true},
    {field: 'vehScenName', headerName: 'Vehicle Scenario Name', lockPosition: true},
    {field: 'propulsionScenario.forecastOrganization', headerName: 'FCORG', lockPosition: true},
    {field: 'propulsionScenario.id', headerName: 'Propulsion Scenario ID', lockPosition: true},
    {field: 'propulsionScenario.propulsionScenarioName', headerName: 'Propulsion Scenario Name', lockPosition: true}
  ];

  startYear: number;
  endYear: number;
  yearRange: number[];

  constructor(readonly propulsionScenarioService: PropulsionScenarioService,
              readonly stateService: StateService,
              readonly excelService: ExcelExportService,
              public dialog: MatDialog,
              public snackBar: FabMessageService) {
  }

  get showSpinner(): boolean {
    return this.loading.active;
  }

  ngOnInit(): void {
    this.selectedScenarioType = 'fcorg';

    // If there is already a selected scenario within the state, then use that selected scenario
    if (this.stateService.isVehicleScenarioSelected) {
      this.selectedWorkingScenario = this.stateService.selectedScenario;
    }
    if (this.selectedWorkingScenario) {
      this.getFilterOptions();
      this.getPropScenYearRange(this.selectedWorkingScenario.propulsionScenario.id);
    } else {
      this.getYearRange();
    }
  }

  /**
   * Gets the output filter options for the pivot table data
   */
  getFilterOptions(): void {
    const isGlobal = this.selectedScenarioType === 'global';
    const id = isGlobal ? this.selectedGlobalScenario.id : this.selectedWorkingScenario.vehScenId;
    this.loading.track(this.propulsionScenarioService.getFiltersForPivotTableData(this.stateService.getUserGmid(), id,
      isGlobal)).subscribe({
      next: (data) => {
        this.allFilterOptions = data;
        this.filteredData = data;
      },
      error: (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.initializeFilterOptions();
      }
    });
  }

  /**
   * Event handler when user wants to confirm their selection for fetching the pivot table data
   */
  onRunReport() {
    const reportRequest: PivotTablePropDataRequestDTO = {
      gmid: this.stateService.getUserGmid(),
      workingScenId: this.selectedScenarioType === 'global' ? this.selectedGlobalScenario.id : this.selectedWorkingScenario.vehScenId,
      isGlobal: this.selectedScenarioType === 'global',
      geographyFlag: this.selectedGeography,
      selectedGeographyFilters: this.geographyOptions.selectedOptions,
      selectedMktDivFilters: this.mktDivOptions.selectedOptions,
      selectedBrandFilters: this.brandOptions.selectedOptions,
      selectedPrgmCodeFilters: this.prgmCodeOptions.selectedOptions,
      showPlanningMarket: this.showPlanningMarket,
      startYear: this.startYear,
      endYear: this.endYear
    }

    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const scenarioString = this.selectedScenarioType === 'global' ?
      this.selectedGlobalScenario.id : this.selectedWorkingScenario.vehScenId;

    const fileName = 'PropPivotData_' + scenarioString + "_" + currDate;
    this.loading.track(this.propulsionScenarioService.getPivotTableData(reportRequest)).subscribe({
      next: (response: any) => {
        this.excelService.exportToExcelFromServer(response, fileName);
      },
      error: (error: any) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.snackBar.displaySuccess('Downloaded Successfully', DISMISS_MSG);
      }
    });
  }

  /**
   * Event handler for when the Geography type changes
   */
  onGeographyChange(): void {
    // Clear out the filter data and re-fetch the filter data
    this.resetAllFilterOptions();
    this.resetAllSelectedFilter();
    this.getFilterOptions();
  }

  /**
   * Event handler for changing the scenario source type
   */
  onSourceChange(): void {
    // Clear out the selected global scenario
    this.selectedGlobalScenario = undefined;
    // Clear out the filter data and re-fetch the filter data
    this.selectedGeography = undefined;
    this.regionOptions.options = [];
    this.regionOptions.selectedOptions = [];
    this.resetAllFilterOptions();
    this.resetAllSelectedFilter();
    if ((this.selectedGlobalScenario && this.selectedScenarioType === 'global')
      || (this.selectedWorkingScenario && this.selectedScenarioType === 'fcorg')) {
      this.getFilterOptions();
    }
  }

  /**
   * Event handler for when a global scenario is selected
   * @param event The event object
   */
  onGlobalScenarioSelect(event: any): void {
    this.selectedGlobalScenario = event.globalScenario;
    this.getFilterOptions();
  }

  /**
   * Opens the pop-up to select a vehicle scenario
   */
  openVehSelectComponent = (): void => {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        isMultiselect: false,
        title: 'Select Vehicle Scenario',
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.selectedWorkingScenario = res.selectedScenarios[0];
        this.getFilterOptions();
      }
    });
  }

  /**
   * gets the year range for the propulsion scenario
   * @param propScenId The propulsion scenario id
   */
  getPropScenYearRange(propScenId: number): void {
    this.loading.track(this.propulsionScenarioService.getYearRange(propScenId)).subscribe({
      next: (data) => {
        if (data && data.length > 0) {
          // If the default year range is a valid range of volumes in the scenario, then use the default year range. else use the year range values from the scenario
          const scenarioMinYear = data[0].MIN_YEAR;
          const scenarioMaxYear = data[0].MAX_YEAR;
          const {startYear, endYear} = getDefaultYearRange();
          if (startYear < scenarioMaxYear) {
            this.startYear = Math.max(scenarioMinYear, startYear);
            this.endYear = Math.min(endYear, scenarioMaxYear);
            this.yearRange = range(Math.min(scenarioMinYear, startYear), Math.max(endYear, scenarioMaxYear) + 1);
          } else {
            this.startYear = scenarioMinYear;
            this.endYear = scenarioMaxYear;
            this.yearRange = range(this.startYear, this.endYear + 1);
          }
        } else {
          this.throwNoVolumeDataError();
        }
      },
      error: (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    });
  }

  /**
   * Gets the year range for the reports
   */
  getYearRange(): void {
    const {yearRange, startYear, endYear} = getDefaultYearRange();
    this.yearRange = yearRange;
    this.startYear = startYear;
    this.endYear = endYear;
  }

  /**
   * Function used to update the filtered data based off of the selected filters for a particular filter type
   * @param filterType the filter type (which filter it is)
   * @param selectedFilters list of filter values that were selected from the filter
   */
  applyFilter(filterType: string, selectedFilters: any): void {
    if (selectedFilters.length > 0) {
      this.filteredData = this.filteredData.filter(detailRecord => selectedFilters.includes(detailRecord[filterType].toUpperCase()));
    }
  }

  /**
   * Event handler whenever a filter changes
   * @param filterType the filter type (which filter it is)
   */
  onFilterChange(filterType: string): void {
    // Reset the filtered data to the original data
    this.filteredData = this.allFilterOptions;
    // Reset the selected filters for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
    // Update the filtered data based off of which filters are selected
    this.applyFilter(this.REGION, this.regionOptions.selectedOptions);
    if (this.selectedGeography === this.SUBREGION_FLAG) {
      this.applyFilter(this.SUBREGION, this.geographyOptions.selectedOptions);
    } else if (this.selectedGeography === this.MKTAREA_FLAG) {
      this.applyFilter(this.MKTAREA, this.geographyOptions.selectedOptions);
    } else {
      this.applyFilter(this.PLNGMKT, this.geographyOptions.selectedOptions);
    }
    this.applyFilter(this.MKTDIV, this.mktDivOptions.selectedOptions);
    this.applyFilter(this.BRAND, this.brandOptions.selectedOptions);
    this.applyFilter(this.PRGMCODE, this.prgmCodeOptions.selectedOptions);
    // Update the filter options for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
  }

  /**
   * Initialize the filter options for all the filters
   */
  initializeFilterOptions(): void {
    this.regionOptions.options = this.setFilterOption(this.REGION)
    this.applyFilter(this.REGION, this.regionOptions.selectedOptions);
    if (this.selectedGeography === this.SUBREGION_FLAG) {
      this.geographyOptions.options = this.setFilterOption(this.SUBREGION);
      this.setCascadingFilterOptions(this.SUBREGION);
    } else if (this.selectedGeography === this.MKTAREA_FLAG) {
      this.geographyOptions.options = this.setFilterOption(this.MKTAREA);
      this.setCascadingFilterOptions(this.MKTAREA);
    } else {
      this.geographyOptions.options = this.setFilterOption(this.PLNGMKT);
      this.setCascadingFilterOptions(this.PLNGMKT);
    }

    // Set year ranges
    if (this.selectedScenarioType === 'fcorg') {
      this.getPropScenYearRange(this.selectedWorkingScenario.propulsionScenario.id);
    } else {
      this.getYearRange();
    }
  }

  /**
   * Helper function to return back a list of available filter options for the specified filter
   * @param filterFieldName the name of the filter to set the filter options for
   */
  setFilterOption(filterFieldName: string): string[] {
    return [...new Set(this.filteredData
    .sort((a, b) => a[filterFieldName].toUpperCase() > b[filterFieldName].toUpperCase() ? 1 : -1)
    .map(detailRecord => detailRecord[filterFieldName].toUpperCase()))];
  }

  /**
   * Function used to set the filter options for the passed in filter + any of the cascaded filters below it
   * @param filterType the filter type (which filter it is)
   */
  setCascadingFilterOptions(filterType: string): void {
    switch (filterType) {
      case this.REGION:
        if (this.selectedGeography === this.SUBREGION_FLAG) {
          this.geographyOptions.options = this.setFilterOption(this.SUBREGION);
          this.setCascadingFilterOptions(this.SUBREGION);
        } else if (this.selectedGeography === this.MKTAREA_FLAG) {
          this.geographyOptions.options = this.setFilterOption(this.MKTAREA);
          this.setCascadingFilterOptions(this.MKTAREA);
        } else {
          this.geographyOptions.options = this.setFilterOption(this.PLNGMKT);
          this.setCascadingFilterOptions(this.PLNGMKT);
        }
        this.geographyOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.SUBREGION:
        this.mktDivOptions.options = this.setFilterOption(this.MKTDIV);
        this.mktDivOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.MKTAREA:
        this.mktDivOptions.options = this.setFilterOption(this.MKTDIV);
        this.mktDivOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.PLNGMKT:
        this.mktDivOptions.options = this.setFilterOption(this.MKTDIV);
        this.mktDivOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.MKTDIV:
        this.brandOptions.options = this.setFilterOption(this.BRAND);
        this.brandOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.BRAND:
        this.prgmCodeOptions.options = this.setFilterOption(this.PRGMCODE);
        this.prgmCodeOptions.selectedOptions = [];
    }
  }

  /**
   * Resets the output filters for the pivot table data
   */
  resetAllFilterOptions(): void {
    // Clear the filter options
    this.geographyOptions.options = [];
    this.mktDivOptions.options = [];
    this.brandOptions.options = [];
    this.prgmCodeOptions.options = [];
  }

  /**
   * Resets all the selected filters from the filter dropdowns
   */
  resetAllSelectedFilter(): void {
    // Clear the selected filters
    this.geographyOptions.selectedOptions = [];
    this.mktDivOptions.selectedOptions = [];
    this.brandOptions.selectedOptions = [];
    this.prgmCodeOptions.selectedOptions = [];
  }

  /**
   * Checks if a scenario is selected
   */
  isScenarioSelected(): boolean {
    return (this.selectedWorkingScenario && this.selectedScenarioType === 'fcorg')
      || (this.selectedGlobalScenario && this.selectedScenarioType === 'global');
  }

  /**
   * Reset the year values for the report and throw an error indicating that no volume data was found
   */
  throwNoVolumeDataError(): void {
    this.startYear = undefined;
    this.endYear = undefined;
    this.yearRange = [];
    this.snackBar.displayError('Unable to get year range because there is no volume data.', DISMISS_MSG);
  }
}
